import { useState, forwardRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Phone, MapPin, Mail } from "lucide-react";
import { WhatsAppIcon } from "./icons";
import { api, BUSINESS } from "@/lib/api";
import { trackConversion } from "@/lib/gtag";
import { Reveal } from "./motion";

const Contact = forwardRef((props, ref) => {
  const [form, setForm] = useState({ name: "", phone: "", location: props.defaultLocation || "", requirement: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [waUrl, setWaUrl] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.location || !form.requirement) {
      toast.error("Please fill name, phone, location and requirement.");
      return;
    }
    if (!/^[0-9+\-\s]{7,15}$/.test(form.phone)) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setLoading(true);
    const message = [
      "Hello Sudeeksha Invisible Grills,",
      "",
      "I received a new website enquiry.",
      "",
      `Customer Name: ${form.name}`,
      `Phone Number: ${form.phone}`,
      `Locality / Area: ${form.location}`,
      `Email: ${form.email || "Not provided"}`,
      `Requirement: ${form.requirement}`,
      "",
      "Please contact this customer regarding their invisible grills enquiry.",
    ].join("\n");
    const url = `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(message)}`;
    setWaUrl(url);
    try {
      await api.post("/leads", form);
    } catch (err) {
      // Lead is still delivered via WhatsApp — never block the redirect on a save failure
    }
    // Google Ads conversion tracking — fires on enquiry submission
    trackConversion();
    window.open(url, "_blank", "noopener");
    setForm({ name: "", phone: "", location: props.defaultLocation || "", requirement: "", email: "" });
    setDone(true);
    setLoading(false);
  };

  const waText = encodeURIComponent("Hi Sudeeksha, I'd like a free quote for invisible grills.");

  return (
    <section id="contact" ref={ref} className="py-24 md:py-36 bg-steel-900 text-white scroll-mt-20">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-label text-xs tracking-[0.3em] uppercase text-steel-400 mb-4">Free quote</p>
            <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-none mb-6">
              Get your <span className="text-steel-500">no-obligation</span> estimate.
            </h2>
            <p className="text-steel-300 leading-relaxed mb-10 max-w-md">
              Tell us about your balcony or windows and we&apos;ll get back with a transparent price — usually the same day.
            </p>
            <div className="space-y-4">
              <a href={`tel:+91${BUSINESS.phone}`} onClick={trackConversion} data-testid="contact-call" className="flex items-center gap-4 border border-steel-700 p-5 hover:border-steel-500 transition-colors">
                <Phone /> <div><div className="font-label text-xs uppercase tracking-wider text-steel-400">Call us</div><div className="font-display font-bold text-lg">{BUSINESS.phoneDisplay}</div></div>
              </a>
              <a href={`https://wa.me/${BUSINESS.whatsapp}?text=${waText}`} target="_blank" rel="noreferrer" onClick={trackConversion} data-testid="contact-whatsapp" className="flex items-center gap-4 border border-steel-700 p-5 hover:border-steel-500 transition-colors">
                <WhatsAppIcon size={24} className="text-[#25D366]" /> <div><div className="font-label text-xs uppercase tracking-wider text-steel-400">WhatsApp</div><div className="font-display font-bold text-lg">Chat with us now</div></div>
              </a>
              <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="flex items-center gap-4 border border-steel-700 p-5 hover:border-steel-500 transition-colors">
                <Mail /> <div className="min-w-0"><div className="font-label text-xs uppercase tracking-wider text-steel-400">Email</div><div className="font-display font-bold text-lg break-all">{BUSINESS.email}</div></div>
              </a>
            </div>

            <div className="mt-6" data-testid="contact-map">
              <div className="font-label text-xs uppercase tracking-wider text-steel-400 mb-3 flex items-center gap-2">
                <MapPin size={14} /> Serving all of Hyderabad
              </div>
              <div className="border border-steel-700 overflow-hidden">
                <iframe
                  title="Sudeeksha Invisible Grills — Hyderabad service area"
                  src="https://maps.google.com/maps?q=Hyderabad%2C%20Telangana&t=&z=11&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-[240px] grayscale-[0.2]"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {done ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border border-steel-600 bg-steel-800 p-12 text-center" data-testid="lead-success">
                <div className="font-display font-black text-3xl mb-3">Your enquiry is ready.</div>
                <p className="text-steel-300 mb-6">Please send the message in WhatsApp to complete your enquiry.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a href={waUrl} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white font-bold px-6 py-3 hover:bg-[#1fb857] transition-colors inline-flex items-center justify-center gap-2" data-testid="lead-whatsapp-open">
                    <WhatsAppIcon size={18} /> Open WhatsApp
                  </a>
                  <button onClick={() => setDone(false)} className="border border-steel-500 px-6 py-3 hover:bg-steel-700 transition-colors" data-testid="lead-another">Submit another</button>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="lead-form">
                <Field label="Your Name *" value={form.name} onChange={set("name")} testid="lead-name" />
                <Field label="Phone Number *" value={form.phone} onChange={set("phone")} testid="lead-phone" />
                <Field label="Locality / Area *" value={form.location} onChange={set("location")} testid="lead-location" placeholder="e.g. Gachibowli" />
                <Field label="Email (optional)" value={form.email} onChange={set("email")} testid="lead-email" />
                <div className="sm:col-span-2 flex flex-col gap-2">
                  <label className="font-label text-xs uppercase tracking-wider text-steel-400">Requirement *</label>
                  <textarea
                    data-testid="lead-requirement"
                    value={form.requirement}
                    onChange={set("requirement")}
                    rows={4}
                    placeholder="e.g. 2 balconies on 8th floor, SS316 grade"
                    className="bg-steel-800 border border-steel-700 px-4 py-3 text-white placeholder:text-steel-500 focus:outline-none focus:ring-1 focus:ring-steel-400 focus:border-steel-400 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="lead-submit"
                  className="sm:col-span-2 bg-white text-steel-900 font-bold px-8 py-4 flex items-center justify-center gap-3 hover:bg-steel-100 transition-colors disabled:opacity-60"
                >
                  {loading ? <><Loader2 className="animate-spin" size={18} /> Sending...</> : <><WhatsAppIcon size={18} /> Send Enquiry on WhatsApp</>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
});

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-label text-xs uppercase tracking-wider text-steel-400">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="bg-steel-800 border border-steel-700 px-4 py-3 text-white placeholder:text-steel-500 focus:outline-none focus:ring-1 focus:ring-steel-400 focus:border-steel-400 transition-colors"
      />
    </div>
  );
}

Contact.displayName = "Contact";
export default Contact;
